/**
 * Executive Audit Hook
 * FERP UNICORN 5: C-Level audit and compliance intelligence
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';

const GET_EXECUTIVE_AUDIT_INTELLIGENCE = `
  query GetExecutiveAuditIntelligence($input: ExecutiveAuditInput!) {
    getExecutiveAuditIntelligence(input: $input) {
      impactMatrix {
        workflowId
        workflowName
        impactLevel
        affectedUsers
        financialImpact
        regulatoryRisk
        dependencies
      }
      willfulIgnoranceRisks {
        issue
        severity
        acknowledgmentRequired
        deadline
        consequences
        mitigation
      }
      boardMetrics {
        totalWorkflows
        lifeCriticalWorkflows
        complianceScore
        auditTrailIntegrity
        regulatoryViolations
        insuranceLiability
      }
      regulatoryPreparedness {
        framework
        readinessScore
        gaps {
          requirement
          currentState
          remediation
          deadline
        }
        nextAuditDate
      }
      fiduciaryResponsibilities {
        fulfilled
        outstanding {
          responsibility
          deadline
          priority
        }
      }
    }
  }
`;

export interface ImpactMatrixEntry {
  workflowId: string;
  workflowName: string;
  impactLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  affectedUsers: number;
  financialImpact: number;
  regulatoryRisk: string;
  dependencies: string[];
}

export interface WillfulIgnoranceRisk {
  issue: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  acknowledgmentRequired: boolean;
  deadline: string;
  consequences: string;
  mitigation: string;
}

export interface BoardMetrics {
  totalWorkflows: number;
  lifeCriticalWorkflows: number;
  complianceScore: number;
  auditTrailIntegrity: number;
  regulatoryViolations: number;
  insuranceLiability: number;
}

export interface RegulatoryPreparedness {
  framework: string;
  readinessScore: number;
  gaps: Array<{
    requirement: string;
    currentState: string;
    remediation: string;
    deadline: string;
  }>;
  nextAuditDate: string;
}

export interface FiduciaryResponsibilities {
  fulfilled: number;
  outstanding: Array<{
    responsibility: string;
    deadline: string;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
  }>;
}

export interface ExecutiveAuditIntelligence {
  impactMatrix: ImpactMatrixEntry[];
  willfulIgnoranceRisks: WillfulIgnoranceRisk[];
  boardMetrics: BoardMetrics;
  regulatoryPreparedness: RegulatoryPreparedness[];
  fiduciaryResponsibilities: FiduciaryResponsibilities;
}

export function useExecutiveAudit(timeRange: { start: Date; end: Date }) {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();
  const [intelligence, setIntelligence] = useState<ExecutiveAuditIntelligence | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchIntelligence = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: GET_EXECUTIVE_AUDIT_INTELLIGENCE,
        variables: {
          input: {
            tenantId,
            startDate: timeRange.start.toISOString(),
            endDate: timeRange.end.toISOString()
          }
        }
      });

      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }

      setIntelligence(result.data?.getExecutiveAuditIntelligence);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch intelligence'));
    } finally {
      setLoading(false);
    }
  }, [client, tenantId, timeRange]);

  useEffect(() => {
    if (tenantId) {
      fetchIntelligence();
    }
  }, [tenantId, fetchIntelligence]);

  // Poll every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchIntelligence, 300000);
    return () => clearInterval(interval);
  }, [fetchIntelligence]);

  return {
    intelligence,
    impactMatrix: intelligence?.impactMatrix || [],
    willfulIgnoranceRisks: intelligence?.willfulIgnoranceRisks || [],
    boardMetrics: intelligence?.boardMetrics || null,
    regulatoryPreparedness: intelligence?.regulatoryPreparedness || [],
    fiduciaryResponsibilities: intelligence?.fiduciaryResponsibilities || null,
    loading,
    error,
    refetch: fetchIntelligence
  };
}
